import { useEffect, useRef, useState } from 'react';
import type { Stream } from '../api/client.ts';
import { t } from '../i18n/index.ts';

interface Props {
  stream: Stream | null;
  playing: boolean;
  onToggle: (playing: boolean) => void;
}

/**
 * Listening to the broadcast as it happens.
 *
 * Plays the source directly rather than anything on disk. The archive trails
 * the broadcast by up to ~33s because of ffmpeg's write buffer (D9), so it
 * cannot serve the live edge at all — and this is the moment a producer most
 * wants to hear what is going out right now. The CDN sends
 * `Access-Control-Allow-Origin: *`, so the browser fetches it itself and fifty
 * simultaneous listeners cost this server nothing (spec §12).
 *
 * Shape follows design.md §5: a gold dot that pulses while live, beside the
 * `live.now` label.
 */
export function LiveButton({ stream, playing, onToggle }: Props): React.ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [failed, setFailed] = useState(false);
  const url = stream?.live_url ?? null;

  // Held in a ref rather than listed as a dependency. The caller passes an
  // inline arrow, so its identity changes on every render; depending on it made
  // this effect re-run, call load() again, and abort the play() already in
  // flight — which then rejected and switched the button back off.
  const onToggleRef = useRef(onToggle);
  useEffect(() => { onToggleRef.current = onToggle; }, [onToggle]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !url) return;

    if (!playing) {
      audio.pause();
      return;
    }

    setFailed(false);
    // A live stream has no meaningful position, so always start at the edge
    // rather than resuming wherever the element happens to be.
    audio.load();
    void audio.play().catch((err: unknown) => {
      if (err instanceof DOMException) {
        // Refusing to start without a gesture is not a failure: the producer
        // presses the button again and it plays.
        if (err.name === 'NotAllowedError') return;
        // A play cut short by a newer load() is not a failure either.
        if (err.name === 'AbortError') return;
      }
      setFailed(true);
      onToggleRef.current(false);
    });
  }, [playing, url]);

  const unavailable = !url || (stream !== null && !stream.is_live);

  return (
    <>
      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="none"
          onError={(e) => {
            if (!e.currentTarget.error) return;
            setFailed(true);
            onToggle(false);
          }}
          // Something else started playing and stopped this; the button has to
          // stop claiming to be live.
          onPause={() => { if (playing) onToggleRef.current(false); }}
        />
      )}
      <button
        type="button"
        className={`live-button ${playing ? 'on' : ''}`}
        disabled={unavailable}
        aria-pressed={playing}
        title={failed ? t('live.error') : unavailable ? t('live.unavailable') : undefined}
        onClick={() => onToggle(!playing)}
      >
        <span className="live-dot" aria-hidden="true" />
        {playing ? t('live.now') : t('live.listen')}
      </button>
    </>
  );
}
